import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Box,
  Divider,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  stepToActiveStep,
  useCurrentAppointmentRequiresRtg,
  useWizardContext,
} from "./AppointmentWizardContext";
import { useWizardSteps } from "./hooks/useWizardSteps";
import { useSnackbarMessage } from "../Snackbar";
import * as api from "../../services/api";
import { status } from "shared/src/appointment.mjs";
import { formData, FormErrorList, useFormAction } from "../../form";
import { ProductSelector } from "../ProductSelector";
import { useCurrentLocation } from "../../context/LocationContext";
import { ServiceSelector } from "../ServiceSelector";

export default function NotesForReceptionForm({ appointmentId }) {
  const { t } = useTranslation();
  const {
    rtgNotRequired,
    setRtgNotRequired,
    appointmentNeeded,
    setAppointmentNeeded,
    setAppointmentNeededTouched,
    rtgCreated,
    activeStep,
    _setActiveStep,
    clientData,
  } = useWizardContext();
  const { getNextValidStep } = useWizardSteps();
  const displaySnackbarMessage = useSnackbarMessage();
  const location = useCurrentLocation();

  const requiresRtgForThisAppointment = useCurrentAppointmentRequiresRtg();
  const [reservationUnit, setReservationUnit] = useState(
    "earliestAvailableDate",
  );
  const [productId, setProductId] = useState(
    "59b7da94-8ce7-4bd1-bdce-f981a72c421e",
  );

  const handleAppointmentNeededChange = (e) => {
    const val = e.target.value;
    setAppointmentNeeded(val);
    setAppointmentNeededTouched(true);

    if (val === "no") {
      setReservationUnit("earliestAvailableDate");
    } else if (val === "yes") {
      setReservationUnit("weeks");
    }
  };

  const buildReceptionistNotePayload = (
    appointmentId,
    notes,
    needNewAppointment,
  ) => {
    const base = {
      appointment_id: appointmentId,
      note_type: "receptionist",
      note_content: notes?.note_content ?? "",
      doctor_required: Boolean(notes?.doctor_required),
    };

    if (needNewAppointment === "no") {
      return {
        ...base,
        service_id: null,
        reservation_unit: null,
        reservation_count: null,
      };
    }

    const unit = notes?.reservation_unit ?? null;
    const count =
      unit === "earliestAvailableDate"
        ? 0
        : Number.isFinite(Number(notes?.reservation_count))
          ? Number(notes?.reservation_count)
          : null;

    return {
      ...base,
      service_id: notes?.service_id || null,
      reservation_unit: unit,
      reservation_count: count,
    };
  };

  const { errors, onSubmit } = useFormAction(
    async (e) => {
      const data = formData(e.target);

      const nextStep = getNextValidStep(activeStep);

      return api.patchAppointment({
        id: appointmentId,
        status_id: status.TREATED,
        active_step: stepToActiveStep(nextStep),
        notes: [
          buildReceptionistNotePayload(
            appointmentId,
            data.note,
            appointmentNeeded,
          ),
        ],
      });
    },
    () => {
      const nextStep = getNextValidStep(activeStep);
      _setActiveStep(nextStep);
      displaySnackbarMessage({
        title: t("Zápis pro recepci byl úspěšně odeslán."),
      });
    },
  );

  return (
    <>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {t("appointmentNotes.notesForReception")}
      </Typography>
      <form id="appointmentWizard" onSubmit={onSubmit}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid>
            <FormControl
              sx={{ minWidth: 240 }}
              required={appointmentNeeded === "yes"}
            >
              <ProductSelector
                company_id={location.company_id}
                client_id={clientData.id}
                required={appointmentNeeded === "yes"}
                onChange={(v) => setProductId(v?.id)}
                default_product_id={productId}
              />
              <Box sx={{ mt: 1 }}>
                <ServiceSelector
                  key={productId ?? ""}
                  location_id={location.id}
                  product_id={productId}
                  required={appointmentNeeded === "yes"}
                  name="note.service_id"
                  truthyFilters={["create_reservation"]}
                />
              </Box>
            </FormControl>
          </Grid>

          <Grid>
            <FormControlLabel
              control={<Checkbox name="note.doctor_required" />}
              label={t("appointmentNotes.doctorRequired")}
            />
          </Grid>

          <Grid>
            <TextField
              name="note.note_content"
              id="note_content"
              label={t("appointmentNotes.quickInfo")}
              multiline
              fullWidth
              rows={2}
            />
          </Grid>
        </Grid>

        <FormControl component="fieldset" sx={{ mb: 2 }} required>
          <FormLabel component="legend">
            {t("appointmentNotes.appointmentNeeded")}
          </FormLabel>
          <RadioGroup
            row
            value={appointmentNeeded}
            onChange={handleAppointmentNeededChange}
          >
            <FormControlLabel
              name="appointmentNeeded"
              value="yes"
              control={<Radio />}
              label={t("appointmentNotes.needNewAppointment")}
              required
            />
            <FormControlLabel
              name="appointmentNeeded"
              value="no"
              control={<Radio />}
              label={t("appointmentNotes.noNewAppointment")}
              required
            />
          </RadioGroup>
        </FormControl>

        {appointmentNeeded === "yes" && (
          <Grid container spacing={2} alignItems="center">
            <Grid>
              <TextField
                sx={{ width: 120 }}
                name="note.reservation_count"
                id="reservation_count"
                label={t("appointmentNotes.number")}
                type="number"
                slotProps={{ htmlInput: { min: 1 } }}
                required={reservationUnit !== "earliestAvailableDate"}
              />
            </Grid>

            <Grid>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>{t("appointmentNotes.unit")}</InputLabel>
                <Select
                  name="note.reservation_unit"
                  value={reservationUnit}
                  onChange={(e) => setReservationUnit(e.target.value)}
                >
                  <MenuItem value="earliestAvailableDate">
                    {t("appointmentNotes.earliestAvailableDate")}
                  </MenuItem>
                  <MenuItem value="days">{t("appointmentNotes.days")}</MenuItem>
                  <MenuItem value="weeks">
                    {t("appointmentNotes.weeks")}
                  </MenuItem>
                  <MenuItem value="months">
                    {t("appointmentNotes.months")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {requiresRtgForThisAppointment && !rtgCreated && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 3 }}>
              <Typography variant="h2" sx={{ mb: 2 }}>
                {t("appointmentNotes.rtgTeleSection", "RTG/TELE")}
              </Typography>

              <Typography variant="body1" sx={{ mb: 2 }}>
                Rentgen je u této schůzky povinný. Vytvořte ad hoc RTG nebo
                potvrďte, že RTG/TELE neproběhne.
              </Typography>

              <FormControlLabel
                required
                control={
                  <Checkbox
                    name="no_rtg"
                    checked={rtgNotRequired}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setRtgNotRequired(next);
                    }}
                    color="primary"
                  />
                }
                label={t(
                  "appointmentNotes.rtgNotRequired",
                  "RTG/TELE neproběhne",
                )}
              />
            </Box>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        <FormErrorList formErrors={errors} />
      </form>
    </>
  );
}
